import type { VerificationDocumentType } from "app/types/documentType";
import { DocumentCategory, DocumentsForUpload } from "app/utils/const";
import { IntlShape } from "react-intl";

export class VerificationDocumentsHelper {
  static verifyIdUploaded(
    documentList: VerificationDocumentType[],
    intl: IntlShape
  ): string {
    // 1. Must have at least one identity document.
    // 2. If a National Identity Card is present, both sides must be there.

    let hasIdentity = false;
    let hasNationalIdentityCardFront = false;
    let hasNationalIdentityCardBack = false;

    for (const doc of documentList) {
      if (doc.documentType === DocumentCategory.IDENTITY) {
        hasIdentity = true;
        if (doc.documentSubType === DocumentsForUpload.ID_CARD_FRONT) {
          hasNationalIdentityCardFront = true;
        } else if (doc.documentSubType === DocumentsForUpload.ID_CARD_BACK) {
          hasNationalIdentityCardBack = true;
        } else {
          return "";
        }
      }
    }

    const errorAtLeastOneMessage = intl.formatMessage({
      id: "account-verification-error-at-least-one-id-document",
      defaultMessage:
        "At least one identity document must be uploaded before account verification can begin",
    });
    const errorTwoIdDocuments = intl.formatMessage({
      id: "account-verification-error-front-and-back",
      defaultMessage:
        "Please upload an image of the front and back of the ID card.",
    });

    if (!hasIdentity) {
      return errorAtLeastOneMessage;
    }
    if (!hasNationalIdentityCardFront || !hasNationalIdentityCardBack) {
      return errorTwoIdDocuments;
    }

    return "";
  }

  static checkIfFrontOrBackOnlyUploaded(
    documentList: VerificationDocumentType[]
  ): string | null {
    // Check if only one side of the idcard has been uploaded
    // If only one side has been uploaded return which that side
    // Otherwise return null
    let hasFront = false;
    let hasBack = false;

    for (const doc of documentList) {
      if (doc.documentType === DocumentCategory.IDENTITY) {
        if (doc.documentSubType === DocumentsForUpload.ID_CARD_FRONT) {
          hasFront = true;
        } else if (doc.documentSubType === DocumentsForUpload.ID_CARD_BACK) {
          hasBack = true;
        } else {
          return null;
        }
      }
    }

    if (hasFront && !hasBack) {
      return DocumentsForUpload.ID_CARD_FRONT.toString();
    } else if (!hasFront && hasBack) {
      return DocumentsForUpload.ID_CARD_BACK.toString();
    } else {
      return null;
    }
  }

  static verifyProofOfAddressUploaded(
    documentList: VerificationDocumentType[],
    intl: IntlShape
  ): string {
    let hasProofOfAddress = false;

    for (const doc of documentList) {
      if (doc.documentType === DocumentsForUpload.POA) {
        hasProofOfAddress = true;
        break;
      }
    }
    const errorPOA = intl.formatMessage({
      id: "account-verification-error-at-least-one-poa-document",
      defaultMessage:
        "At least one proof of address document must be uploaded before account verification can begin",
    });

    return hasProofOfAddress ? "" : errorPOA;
  }

  static getText(id: string, intl: IntlShape) {
    switch (id) {
      case "photoIdTitle":
        return intl.formatMessage({
          id: "account-verification-choose-photo-id-title",
          defaultMessage: "1. Photo ID",
        });
      case "photoIdDescription":
        return intl.formatMessage({
          id: "account-verification-photo-id-description",
          defaultMessage:
            "This could be your passport, driving licence or National ID card",
        });
      case "photoIdButtonText":
        return intl.formatMessage({
          id: "account-verification-photo-id-button",
          defaultMessage: "Upload Photo ID",
        });
      case "poaButtonText":
        return intl.formatMessage({
          id: "account-verification-proof-of-address-button",
          defaultMessage: "Upload Proof of Address",
        });
      case "poaTitle":
        return intl.formatMessage({
          id: "account-verification-choose-proof-of-address-title",
          defaultMessage: "2. Proof of Address",
        });
      case "poaDescription":
        return intl.formatMessage({
          id: "account-verification-proof-of-address-description",
          defaultMessage:
            "This could be a utility bill or bank statement less then 3 months old",
        });
      case "account-verification-error-at-least-one-document":
        return intl.formatMessage({
          id: "account-verification-error-at-least-one-document",
          defaultMessage:
            "At least one identity document must be uploaded before account verification can begin",
        });
      default:
        return "";
    }
  }
  static convertMegabytesToBytes(megabytes: number): number {
    const bytesPerMegabyte = 1024 * 1024; // 1 MB = 1024 KB, 1 KB = 1024 B
    return megabytes * bytesPerMegabyte;
  }
}
